/**
 * The `expect` layer that attaches DOM comparison to a visual assertion.
 *
 * Wraps `toHaveScreenshot` and `toMatchVisualBaseline` so that both capture a
 * DOM snapshot, compare it against a committed baseline, and record which
 * elements differ. The pixel comparison is untouched — this runs either side of
 * it and never changes its verdict.
 *
 * Composed as a layer rather than folded into `assertion-tracker.ts`: that
 * module records API assertions, and giving it visual concerns too would give
 * it two jobs. It also returns early for anything without an API call id, which
 * is exactly why `expect(page).toHaveScreenshot()` reaches raw Playwright today.
 */

import { basename, extname } from 'node:path';
import { writeFileSync } from 'node:fs';
import { existsSync } from 'node:fs';
import type { DomChange } from '@testrelic/core';
import {
  captureDom,
  domBaselinePath,
  readDomBaseline,
  writeDomBaseline,
  serializeSnapshot,
  type BoundedTarget,
  type EvaluateTarget,
} from './dom-snapshot.js';
import { diffDomSnapshots } from './dom-diff.js';
import { supportsSnapshotPathOptions, warnSnapshotPathUnsupported } from './snapshot-path-support.js';
import type { DomSnapshot } from './dom-capture.js';

/** Attachment carrying the DOM comparison from the worker to the reporter. */
export const VISUAL_DOM_ATTACHMENT = 'testrelic-visual-dom';

/** One comparison's DOM result, as it crosses the process boundary. */
export interface VisualDomRecord {
  readonly stem: string;
  readonly changes: readonly DomChange[];
  readonly total: number;
  /** Of the total, how many changed rather than merely shifted. */
  readonly rootCauses: number;
  readonly truncated: boolean;
  readonly captureBox: readonly [number, number, number, number];
  /** Absolute paths for the reporter to copy into the run's artifacts. */
  readonly baselineDomPath?: string;
  readonly actualDomPath?: string;
}

const VISUAL_MATCHERS = new Set(['toHaveScreenshot', 'toMatchVisualBaseline']);

interface MinimalTestInfo {
  outputPath(name: string): string;
  attachments: ReadonlyArray<{ name: string }>;
  snapshotPath(name: string, options: { kind: 'screenshot' }): string;
  attach(name: string, options: { body?: Buffer; contentType?: string }): Promise<void>;
  config: { updateSnapshots?: unknown };
}

interface LocatorLike extends BoundedTarget {
  page(): EvaluateTarget;
}

// SAFETY: the received value of an `expect(...)` call is genuinely unknown.
// These two guards check for the shape each path needs and nothing more.
function isLocatorLike(value: unknown): value is LocatorLike {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.boundingBox === 'function' && typeof v.page === 'function';
}

function isPageLike(value: unknown): value is EvaluateTarget {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.evaluate === 'function' && typeof v.screenshot === 'function';
}

/** The snapshot name, whichever of the two call shapes was used. */
function snapshotNameOf(args: readonly unknown[]): string | null {
  const first = args[0];
  if (typeof first === 'string') return first;
  if (Array.isArray(first)) return first.join('/');
  if (typeof first === 'object' && first !== null) {
    const name = (first as Record<string, unknown>).name;
    if (typeof name === 'string') return name;
  }
  return null;
}

/** The options object, which may be the first or the second argument. */
function optionsOf(args: readonly unknown[]): Record<string, unknown> {
  for (const arg of args) {
    if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
      return arg as Record<string, unknown>;
    }
  }
  return {};
}

function maskLocatorsOf(options: Record<string, unknown>): BoundedTarget[] {
  const mask = options.mask;
  if (!Array.isArray(mask)) return [];
  return mask.filter((entry): entry is BoundedTarget => {
    return (
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as Record<string, unknown>).boundingBox === 'function'
    );
  });
}

/** `toHaveScreenshot` rejects a bare name; the resolved path needs the same form. */
function withPngExtension(name: string): string {
  return /\.(png|jpg|jpeg)$/i.test(name) ? name : `${name}.png`;
}

interface DomComparisonInput {
  readonly received: unknown;
  readonly args: readonly unknown[];
  readonly testInfo: MinimalTestInfo;
  /** Attachment count before the assertion ran, to spot the ones it added. */
  readonly attachedBefore: number;
}

const ROLE_SUFFIX_RE = /^(.+)-(expected|actual|diff|previous)\.[A-Za-z0-9]+$/;

/**
 * The stem the reporter will group this comparison under.
 *
 * It must be read back from the attachments the assertion just produced, not
 * derived from the snapshot path: Playwright names attachments after the
 * resolved OUTPUT path, and the two are genuinely different — a snapshot at
 * `drift-target-win32.png` attaches as `drift-target-expected.png`. Deriving it
 * the obvious way silently produced a stem that matched nothing, so every
 * natively-asserted comparison lost its element list.
 *
 * Falls back to the snapshot basename for a passing assertion, which attaches
 * no images — that is the path `toMatchVisualBaseline` takes, and it names its
 * own attachment with exactly that stem.
 */
function resolveStem(testInfo: MinimalTestInfo, attachedBefore: number, fallback: string): string {
  const attachments = testInfo.attachments;
  for (let i = attachedBefore; i < attachments.length; i++) {
    const match = ROLE_SUFFIX_RE.exec(attachments[i]?.name ?? '');
    if (match) return match[1] as string;
  }
  return fallback;
}

/** Capture the DOM of whatever is about to be screenshotted. */
async function capture(input: DomComparisonInput): Promise<DomSnapshot | null> {
  const options = optionsOf(input.args);
  const locator = isLocatorLike(input.received) ? input.received : null;
  const target = locator ? locator.page() : (input.received as EvaluateTarget);

  return captureDom({
    target,
    bounded: locator,
    maskLocators: maskLocatorsOf(options),
    fullPage: options.fullPage === true,
  });
}

/** True when the run was told to rewrite baselines rather than compare against them. */
function isUpdatingSnapshots(testInfo: MinimalTestInfo): boolean {
  const mode = testInfo.config?.updateSnapshots;
  return mode === 'all' || mode === 'changed';
}

/**
 * Compare the captured DOM against its baseline and record the result.
 *
 * Writes the baseline when there is not one yet, or when the run was invoked
 * with `--update-snapshots` — mirroring exactly what Playwright does with the
 * image beside it, so the two never drift apart.
 */
async function recordDomComparison(input: DomComparisonInput, actual: DomSnapshot): Promise<void> {
  const name = snapshotNameOf(input.args);
  if (!name) return;

  // Resolving the baseline needs the options form of snapshotPath (>= 1.51).
  // Without it the path comes back wrong and this whole feature silently did
  // nothing; say so once rather than leave the user guessing.
  if (!supportsSnapshotPathOptions(input.testInfo)) {
    warnSnapshotPathUnsupported('DOM comparison');
    return;
  }

  const screenshotPath = input.testInfo.snapshotPath(withPngExtension(name), {
    kind: 'screenshot',
  });
  const baselinePath = domBaselinePath(screenshotPath);
  const stem = resolveStem(
    input.testInfo,
    input.attachedBefore,
    basename(screenshotPath, extname(screenshotPath)),
  );

  const serialized = serializeSnapshot(actual);
  const updating = isUpdatingSnapshots(input.testInfo);
  const baseline = updating ? null : readDomBaseline(baselinePath);

  if (!baseline) {
    // No reference to compare against — write one, the way Playwright writes
    // the image baseline in the same situation. Nothing is reported as changed,
    // because nothing was compared.
    writeDomBaseline(baselinePath, serialized.json);
    return;
  }

  const { changes, total, rootCauses } = diffDomSnapshots(baseline, actual);

  let actualDomPath: string | undefined;
  try {
    actualDomPath = input.testInfo.outputPath(`${stem}.actual.dom.json`);
    writeFileSync(actualDomPath, serialized.json, 'utf-8');
  } catch {
    actualDomPath = undefined;
  }

  const record: VisualDomRecord = {
    stem,
    changes,
    total,
    rootCauses,
    truncated: serialized.truncatedBytes || actual.truncatedNodes > 0,
    captureBox: actual.captureBox,
    ...(existsSync(baselinePath) ? { baselineDomPath: baselinePath } : {}),
    ...(actualDomPath ? { actualDomPath } : {}),
  };

  await input.testInfo.attach(VISUAL_DOM_ATTACHMENT, {
    body: Buffer.from(JSON.stringify([record])),
    contentType: 'application/json',
  });
}

type MatcherFn = (...args: unknown[]) => unknown;

/**
 * Wrap an `expect` so visual assertions also compare the DOM.
 *
 * Everything that is not a visual assertion against a Page or Locator passes
 * through untouched, including `.not`, `.soft` and every other matcher.
 */
export function withVisualCapture(
  baseExpect: (...args: unknown[]) => unknown,
  getTestInfo: () => MinimalTestInfo | null,
): (...args: unknown[]) => unknown {
  const wrapped = function visualExpect(received: unknown, ...rest: unknown[]): unknown {
    const matchers = baseExpect(received, ...rest);

    const isTarget = isLocatorLike(received) || isPageLike(received);
    if (!isTarget || typeof matchers !== 'object' || matchers === null) return matchers;

    return new Proxy(matchers as object, {
      get(target, prop, receiver): unknown {
        const value = Reflect.get(target, prop, receiver);
        if (typeof prop !== 'string' || !VISUAL_MATCHERS.has(prop)) return value;
        if (typeof value !== 'function') return value;

        const original = value as MatcherFn;
        return async function domAwareMatcher(...args: unknown[]): Promise<unknown> {
          const testInfo = getTestInfo();

          // Capture BEFORE the assertion: a failing comparison may leave the
          // page mid-retry, and the DOM we want is the one that was rendered.
          let snapshot: DomSnapshot | null = null;
          const attachedBefore = testInfo?.attachments.length ?? 0;
          if (testInfo) {
            try {
              snapshot = await capture({ received, args, testInfo, attachedBefore });
            } catch {
              // DOM capture never changes whether the assertion runs.
            }
          }

          try {
            return await original.apply(target, args);
          } finally {
            if (testInfo && snapshot) {
              try {
                await recordDomComparison({ received, args, testInfo, attachedBefore }, snapshot);
              } catch {
                // A DOM comparison problem must never alter the test outcome.
              }
            }
          }
        };
      },
    });
  };

  // Carry `expect.soft`, `expect.configure`, `expect.extend` and the rest
  // forward; a wrapper that drops them silently breaks unrelated assertions.
  const statics = baseExpect as unknown as Record<string, unknown>;
  const target = wrapped as unknown as Record<string, unknown>;
  for (const key of Object.keys(statics)) {
    target[key] = statics[key];
  }

  return wrapped;
}
